import type { Pubky } from '@/models/models.types';
import { REACH, type ReachType } from '@/stores/home/home.types';

export type SearchReach = Exclude<ReachType, typeof REACH.ME>;

/**
 * Recent search item types
 */
export interface RecentUserSearch {
  id: Pubky;
  searchedAt: number; // Timestamp
}

export interface RecentTagSearch {
  tag: string;
  searchedAt: number; // Timestamp
}

export interface RecentQuerySearch {
  query: string;
  searchedAt: number; // Timestamp
}

/**
 * Search Store State
 */
export interface SearchState {
  /** Session-only selection, independent of Home. */
  reach: SearchReach;
  recentUsers: RecentUserSearch[];
  recentTags: RecentTagSearch[];
  /** Full-text queries submitted via the search input */
  recentQueries: RecentQuerySearch[];
  /** Active tags for optimistic UI - immediately updated, then synced with URL */
  activeTags: string[];
}

/**
 * Search Store Actions
 */
export interface SearchActions {
  setReach: (reach: SearchReach) => void;
  addUser: (userId: Pubky) => void;
  /** Add a tag to recent searches. Tag should be normalized (lowercase, trimmed) before calling */
  addTag: (tag: string) => void;
  /** Add a full-text query to recent searches. Query should be validated (trimmed) before calling */
  addQuery: (query: string) => void;
  /** Clear only recent searches (users, tags, and queries), keep active tags */
  clearRecentSearches: () => void;
  /** Set active tags (used for URL → store sync) */
  setActiveTags: (tags: string[]) => void;
  /** Add a tag to active tags (optimistic update). Tag should be normalized (lowercase, trimmed) before calling */
  addActiveTag: (tag: string) => void;
  /** Remove a tag from active tags (optimistic update). Tag should be normalized (lowercase, trimmed) before calling */
  removeActiveTag: (tag: string) => void;
  /** Reset search store to initial state */
  reset: () => void;
}

/**
 * Complete Search Store Type
 */
export type SearchStore = SearchState & SearchActions;

/**
 * Initial state for search store
 */
export const searchInitialState: SearchState = {
  reach: REACH.ALL,
  recentUsers: [],
  recentTags: [],
  recentQueries: [],
  activeTags: [],
};

/**
 * Action types for DevTools
 */
export enum SearchActionTypes {
  SET_REACH = 'SET_REACH',
  ADD_USER = 'ADD_USER',
  ADD_TAG = 'ADD_TAG',
  ADD_QUERY = 'ADD_QUERY',
  CLEAR_RECENT_SEARCHES = 'CLEAR_RECENT_SEARCHES',
  SET_ACTIVE_TAGS = 'SET_ACTIVE_TAGS',
  ADD_ACTIVE_TAG = 'ADD_ACTIVE_TAG',
  REMOVE_ACTIVE_TAG = 'REMOVE_ACTIVE_TAG',
  RESET = 'RESET',
}
